import { policyResultKeys, type PolicyPlacement } from './policy-order-api'
import { decodePolicyMutation, decodePolicyRefreshStatus, type PolicyRefreshStatus } from '../lib/policy-authoring'
import type { components } from '@/lib/openapi.generated'
import {
  isRecord,
  json,
  nullableNumber,
  nullableString,
  request,
  requireBoolean,
  requireNumber,
  requireString,
  requireTimestamp,
  withQuery,
} from '@/lib/api'

type Schemas = components['schemas']
export const contentUploadPolicyKeys = {
  result: (entityId: string) => policyResultKeys.type(entityId, CONTENT_UPLOAD_POLICY_TYPE),
}
export const CONTENT_UPLOAD_POLICY_TYPE = 'entity:content.media.max-bytes'
export type ContentUploadPolicy = Omit<Schemas['ContentUploadPolicyResponse'], 'selectorGui'> & { selectorGui?: Record<string, unknown> | null }
export type ContentUploadPolicyEdit = Omit<ContentUploadPolicy, 'id' | 'revision' | 'priority'> & { placement?: PolicyPlacement }
export type { PolicyRefreshStatus }
export type ContentUploadPolicyResult = Omit<
  Schemas['ContentUploadPolicyResultResponse'],
  'selectionStatus' | 'refreshStatus'
> & {
  selectionStatus: 'NeverEvaluated' | 'Evaluated'
  refreshStatus: PolicyRefreshStatus
}

function decodePolicy(raw: unknown): ContentUploadPolicy {
  if (!isRecord(raw)) throw new Error('Malformed upload policy')
  return {
    id: requireString(raw.id, 'id'),
    name: requireString(raw.name, 'name'),
    value: requireNumber(raw.value, 'value'),
    selectorExpression: nullableString(raw.selectorExpression, 'selectorExpression'),
    selectorGui: decodeSelectorGui(raw.selectorGui),
    priority: requireNumber(raw.priority, 'priority'),
    revision: requireNumber(raw.revision, 'revision'),
  } satisfies ContentUploadPolicy
}

function decodeSelectorGui(raw: unknown): Record<string, unknown> | null {
  if (raw == null) return null
  if (!isRecord(raw)) throw new Error('Malformed policy selector UI state')
  return raw
}

const decodeMutation = (raw: unknown) =>
  decodePolicyMutation(raw) satisfies Schemas['ContentUploadPolicyMutationResponse']

function decodeResult(raw: unknown): ContentUploadPolicyResult {
  if (!isRecord(raw)) throw new Error('Malformed upload policy result')
  if (raw.selectionStatus !== 'NeverEvaluated' && raw.selectionStatus !== 'Evaluated')
    throw new Error('Unexpected policy selection status')
  return {
    selectionStatus: raw.selectionStatus,
    selectionRevision: nullableNumber(raw.selectionRevision, 'selectionRevision'),
    evaluatedAt: raw.evaluatedAt == null ? null : requireTimestamp(raw.evaluatedAt, 'evaluatedAt'),
    refreshStatus: decodePolicyRefreshStatus(raw.refreshStatus),
    failureCode: nullableString(raw.failureCode, 'failureCode'),
    value: nullableNumber(raw.value, 'value'),
    isDefault: requireBoolean(raw.isDefault, 'isDefault'),
  } satisfies ContentUploadPolicyResult
}

const policiesPath = (entityId: string) => `/api/v1/entities/${entityId}/content/upload-policies`
const policyPath = (entityId: string, policyId: string) => `${policiesPath(entityId)}/${policyId}`

export const contentUploadPolicyApi = {
  get: (entityId: string, policyId: string) =>
    request(policyPath(entityId, policyId), decodePolicy),
  result: (entityId: string) =>
    request(`/api/v1/entities/${entityId}/content/upload-policy-result`, decodeResult),
  create: (entityId: string, edit: ContentUploadPolicyEdit, operationId: string) =>
    request(
      policiesPath(entityId),
      decodeMutation,
      json('POST', {
        ...edit,
        operationId,
        placement: edit.placement ? { ...edit.placement, atStart: false } : undefined,
      } satisfies Schemas['CreateContentUploadPolicyRequest']),
    ),
  update: (entityId: string, policy: ContentUploadPolicy, edit: ContentUploadPolicyEdit) =>
    request(
      policyPath(entityId, policy.id),
      decodeMutation,
      json('PUT', {
        ...edit,
        expectedRevision: policy.revision,
      } satisfies Schemas['UpdateContentUploadPolicyRequest']),
    ),
  delete: (entityId: string, policy: Pick<ContentUploadPolicy, 'id' | 'revision'>) =>
    request(
      withQuery(policyPath(entityId, policy.id), {
        expectedRevision: String(policy.revision),
      }),
      decodeMutation,
      { method: 'DELETE' },
    ),
}
